import type {
  SupportV2MigrationProgress,
  SupportV2MigrationRunProgress,
} from "@redotech/admin-sdk/rpc/schema/support/get-support-v2-migration-progress";
import {
  MigrateTeamToSupportV2RerunKey,
  type MigrateTeamToSupportV2StartMode,
  type MigrateTeamToSupportV2Step,
} from "@redotech/admin-sdk/rpc/schema/support/migrate-team-to-support-v2";
import { RedoBadge } from "@redotech/arbiter/badge/redo-badge";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Card } from "@redotech/arbiter/card";
import { RedoCheckbox } from "@redotech/arbiter/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "@redotech/arbiter/collapse/redo-collapse-section";
import { Divider } from "@redotech/arbiter/divider/divider";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import RefreshIcon from "@redotech/redo-icons/featured-refresh-cw-3.svg";
import PlayIcon from "@redotech/redo-icons/play.svg";
import StopIcon from "@redotech/redo-icons/stop-circle.svg";
import {
  expandSupportV2MigrationSteps,
  getSupportV2MigrationImplicitSteps,
} from "@redotech/redo-support-v2-migration/execution/migration-execution";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import {
  EmptyProgressState,
  FailedRunResumeNotice,
  FailureSamples,
  LatestRunOverview,
  RunHistory,
  RunStatusBadge,
  StepProgressList,
} from "./migrate-to-support-v2-card/progress-components";
import {
  ALL_STEPS,
  formatError,
  formatRunStepPlan,
  getOverallPercentage,
  getRunRequestedSteps,
  isActiveRunStatus,
  isAlreadyActiveResumeError,
  PROGRESS_POLL_INTERVAL_MS,
  STEP_OPTIONS,
} from "./migrate-to-support-v2-card/utils";
import { ReconciliationResults } from "./support-v2-reconciliation-results";

const markRunOptimisticallyRunning = (
  progress: SupportV2MigrationProgress | undefined,
  workflowId: string,
): SupportV2MigrationProgress | undefined => {
  if (!progress?.run || progress.run.workflowId !== workflowId) {
    return progress;
  }

  const updatedAt = new Date().toISOString();
  const runningRun: SupportV2MigrationRunProgress = {
    ...progress.run,
    completedAt: null,
    lastError: null,
    status: "running",
    updatedAt,
  };

  return {
    ...progress,
    history: progress.history.map((run) =>
      run.workflowId === workflowId
        ? {
            ...run,
            completedAt: null,
            lastError: null,
            status: "running",
            updatedAt,
          }
        : run,
    ),
    run: runningRun,
  };
};

export const MigrateToSupportV2Card = memo(function MigrateToSupportV2Card({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const [workflowId, setWorkflowId] = useState<string | undefined>();
  const [cancelWorkflowId, setCancelWorkflowId] = useState<
    string | undefined
  >();
  const [rerunKey, setRerunKey] = useState("");
  const [selectedSteps, setSelectedSteps] = useState<
    ReadonlySet<MigrateTeamToSupportV2Step>
  >(() => new Set(ALL_STEPS));
  const progressQueryKey = useMemo(
    () => ["supportV2MigrationProgress", teamId] as const,
    [teamId],
  );

  const progressQuery = useQuery({
    enabled: !!teamId,
    queryFn: () => rpcClient.getSupportV2MigrationProgress({ teamId }),
    queryKey: progressQueryKey,
    refetchInterval: (query) =>
      isActiveRunStatus(query.state.data?.run?.status)
        ? PROGRESS_POLL_INTERVAL_MS
        : false,
    refetchOnWindowFocus: false,
  });
  const migrationProgress = progressQuery.data;
  const latestRun = migrationProgress?.run;
  const latestFailedRun =
    latestRun?.status === "failed" ? latestRun : undefined;
  const isLive = isActiveRunStatus(latestRun?.status);
  const overallPercentage = getOverallPercentage(migrationProgress);

  const implicitSteps = useMemo(
    () => new Set(getSupportV2MigrationImplicitSteps(selectedSteps)),
    [selectedSteps],
  );
  const isForced = (step: MigrateTeamToSupportV2Step) =>
    implicitSteps.has(step);

  const toggleStep = useHandler((step: MigrateTeamToSupportV2Step) => {
    setSelectedSteps((prev) => {
      if (
        prev.has(step) &&
        getSupportV2MigrationImplicitSteps(prev).includes(step)
      ) {
        return prev;
      }
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        for (const expandedStep of expandSupportV2MigrationSteps([step])) {
          next.add(expandedStep);
        }
      }
      return next;
    });
  });

  const stepsToSend = useMemo<MigrateTeamToSupportV2Step[] | undefined>(() => {
    const expandedSteps = expandSupportV2MigrationSteps(selectedSteps);
    if (expandedSteps.length === ALL_STEPS.length) {
      return undefined;
    }
    return [...expandedSteps];
  }, [selectedSteps]);

  const runAll = stepsToSend === undefined;
  const normalizedRerunKey = rerunKey.trim();
  const rerunKeyError = useMemo(() => {
    if (normalizedRerunKey.length === 0) {
      return undefined;
    }
    const result = MigrateTeamToSupportV2RerunKey.safeParse(normalizedRerunKey);
    return result.success
      ? undefined
      : "Use letters, numbers, underscores, or hyphens; max 64 characters.";
  }, [normalizedRerunKey]);
  const isTerminalRerun = normalizedRerunKey.length > 0;
  const shouldResumeLatestFailedRun =
    latestFailedRun !== undefined && !isTerminalRerun;
  const latestFailedRunSteps = useMemo(
    () => getRunRequestedSteps(latestFailedRun),
    [latestFailedRun],
  );
  const currentStepsToSend = shouldResumeLatestFailedRun
    ? latestFailedRunSteps
    : stepsToSend;
  const migrateButtonText = useMemo(() => {
    if (isTerminalRerun) {
      return "Start separate rerun";
    }
    if (shouldResumeLatestFailedRun) {
      return "Resume failed run";
    }
    if (runAll) {
      return "Run full migration";
    }
    return "Run selected steps";
  }, [isTerminalRerun, runAll, shouldResumeLatestFailedRun]);
  const migrateButtonIcon = shouldResumeLatestFailedRun
    ? RefreshIcon
    : PlayIcon;
  const selectedStepCount = expandSupportV2MigrationSteps(selectedSteps).length;
  const runControlSubtext =
    shouldResumeLatestFailedRun && latestFailedRun
      ? `Resume plan: ${formatRunStepPlan(latestFailedRun)}`
      : `${selectedStepCount}/${ALL_STEPS.length} steps selected`;
  const startMode = useMemo<MigrateTeamToSupportV2StartMode>(() => {
    if (isTerminalRerun) {
      return { rerunKey: normalizedRerunKey, type: "terminal_rerun" };
    }
    if (shouldResumeLatestFailedRun) {
      return { type: "resume_failed" };
    }
    return { type: "start" };
  }, [isTerminalRerun, normalizedRerunKey, shouldResumeLatestFailedRun]);

  const migrateMutation = useMutation({
    mutationFn: () =>
      rpcClient.migrateTeamToSupportV2({
        startMode,
        teamId,
        steps: currentStepsToSend,
      }),
    onSuccess: (result) => {
      setWorkflowId(result.workflowId);
      if (startMode.type === "resume_failed") {
        queryClient.setQueryData<SupportV2MigrationProgress>(
          progressQueryKey,
          (progress) =>
            markRunOptimisticallyRunning(progress, result.workflowId),
        );
      }
      void progressQuery.refetch();
    },
    onError: (error) => {
      if (
        startMode.type === "resume_failed" &&
        latestRun &&
        isAlreadyActiveResumeError(error)
      ) {
        queryClient.setQueryData<SupportV2MigrationProgress>(
          progressQueryKey,
          (progress) =>
            markRunOptimisticallyRunning(progress, latestRun.workflowId),
        );
      }
      void progressQuery.refetch();
    },
  });

  const handleMigrate = useHandler(() => {
    setWorkflowId(undefined);
    migrateMutation.mutate();
  });

  const reconcileMutation = useMutation({
    mutationFn: () => rpcClient.getSupportV2EntityParity({ teamId }),
  });
  const handleReconcile = useHandler(() => {
    reconcileMutation.reset();
    reconcileMutation.mutate();
  });
  const reconciliation = reconcileMutation.data;

  const cancelMutation = useMutation({
    mutationFn: (workflowIdToCancel: string) =>
      rpcClient.cancelSupportV2Migration({
        teamId,
        workflowId: workflowIdToCancel,
      }),
    onSuccess: () => {
      setCancelWorkflowId(undefined);
      void progressQuery.refetch();
    },
  });

  const handleCancelRequest = useHandler((workflowIdToCancel: string) => {
    cancelMutation.reset();
    setCancelWorkflowId(workflowIdToCancel);
  });

  const closeCancelModal = useHandler(() => {
    if (!cancelMutation.isPending) {
      setCancelWorkflowId(undefined);
    }
  });

  return (
    <>
      <Card borderRadius="md" title="Migrate team to support-v2">
        <Flex dir="column" gap="lg" w="full">
          <Flex align="flex-start" gap="md" justify="space-between" wrap="wrap">
            <Flex dir="column" gap="xs" grow={1}>
              <Text fontSize="sm" textColor="secondary">
                Starts the per-team Temporal workflow and reads durable
                migration progress from CockroachDB.
              </Text>
              {latestRun && (
                <Text fontSize="xs" textColor="secondary">
                  Workflow {latestRun.workflowId}
                </Text>
              )}
            </Flex>
            <Flex align="center" gap="xs">
              <RedoBadge
                color={isLive ? "blue" : "gray"}
                size="sm"
                text={isLive ? "Live polling" : "Manual refresh"}
              />
              {latestRun && <RunStatusBadge status={latestRun.status} />}
            </Flex>
          </Flex>

          {progressQuery.isLoading && (
            <Text fontSize="sm" textColor="secondary">
              Loading migration progress...
            </Text>
          )}
          {progressQuery.error && (
            <Text fontSize="sm" textColor="error">
              Progress error: {formatError(progressQuery.error)}
            </Text>
          )}
          {!progressQuery.isLoading && !progressQuery.error && !latestRun && (
            <EmptyProgressState />
          )}

          {latestRun && migrationProgress && (
            <>
              <LatestRunOverview
                cancelPending={cancelMutation.isPending}
                onCancelRun={handleCancelRequest}
                overallPercentage={overallPercentage}
                progress={migrationProgress}
              />
              <StepProgressList steps={migrationProgress.steps} />
              <FailureSamples failures={migrationProgress.failures} />
              <RunHistory
                history={migrationProgress.history}
                latestRunId={latestRun.id}
              />
              {latestFailedRun && !isTerminalRerun && (
                <FailedRunResumeNotice run={latestFailedRun} />
              )}
            </>
          )}

          <Divider />

          <Flex dir="column" gap="md">
            <Flex align="center" justify="space-between" wrap="wrap">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="medium">
                  Run controls
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {runControlSubtext}
                </Text>
              </Flex>
              <RedoButton
                hierarchy="secondary"
                IconLeading={RefreshIcon}
                onClick={() => void progressQuery.refetch()}
                pending={progressQuery.isFetching}
                size="sm"
                text="Refresh"
              />
            </Flex>

            <RedoCollapseSubsection
              defaultCollapsed={shouldResumeLatestFailedRun}
              title="Step selection"
            >
              <Flex dir="column" gap="xs" pl="lg">
                {STEP_OPTIONS.map((option) => (
                  <RedoCheckbox
                    disabled={isForced(option.value)}
                    key={option.value}
                    label={option.label}
                    setValue={() => toggleStep(option.value)}
                    size="sm"
                    value={selectedSteps.has(option.value)}
                  />
                ))}
              </Flex>
            </RedoCollapseSubsection>

            {latestRun && (
              <RedoCollapseSubsection
                defaultCollapsed
                title="Advanced separate rerun"
              >
                <Flex dir="column" gap="sm" pl="lg">
                  <Text fontSize="xs" textColor="secondary">
                    Use only when you want a new workflow instead of resuming
                    the existing run. The key is added to the workflow id.
                  </Text>
                  <RedoTextInput
                    autoCompleteDisabled
                    description="Example: fix_invalid_message_types"
                    errorText={rerunKeyError}
                    htmlPattern="[A-Za-z0-9][A-Za-z0-9_-]{0,63}"
                    label="Separate rerun key"
                    maxLength={64}
                    placeholder="rerun_001"
                    setValue={setRerunKey}
                    size="sm"
                    state={rerunKeyError ? "error" : "default"}
                    value={rerunKey}
                  />
                </Flex>
              </RedoCollapseSubsection>
            )}

            <Flex align="center" gap="md" wrap="wrap">
              <RedoButton
                disabled={
                  migrateMutation.isPending ||
                  (!shouldResumeLatestFailedRun && selectedSteps.size === 0) ||
                  rerunKeyError !== undefined
                }
                hierarchy="primary"
                IconLeading={migrateButtonIcon}
                onClick={handleMigrate}
                pending={migrateMutation.isPending}
                text={migrateButtonText}
              />
              {workflowId && (
                <Text fontSize="sm" fontWeight="medium">
                  Started: {workflowId}
                </Text>
              )}
              {migrateMutation.error && (
                <Text fontSize="sm" fontWeight="medium" textColor="error">
                  Error: {formatError(migrateMutation.error)}
                </Text>
              )}
            </Flex>
          </Flex>

          <Divider />

          <Flex dir="column" gap="md">
            <Flex align="center" justify="space-between" wrap="wrap">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="medium">
                  Reconciliation
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Read-only tag / macro / rule + ticket-count parity (V1 vs V2).
                </Text>
              </Flex>
              <RedoButton
                hierarchy="secondary"
                IconLeading={RefreshIcon}
                onClick={handleReconcile}
                pending={reconcileMutation.isPending}
                size="sm"
                text="Run reconciliation"
              />
            </Flex>

            {reconcileMutation.error && (
              <Text fontSize="sm" textColor="error">
                Error: {formatError(reconcileMutation.error)}
              </Text>
            )}

            {reconciliation && (
              <ReconciliationResults snapshot={reconciliation} />
            )}
          </Flex>
        </Flex>
      </Card>
      {cancelWorkflowId && (
        <RedoModal
          headerIconAlignment="inline"
          modalSize="sm"
          onModalCloseRequested={closeCancelModal}
          primaryButton={{
            IconLeading: StopIcon,
            onClick: async () => {
              await cancelMutation.mutateAsync(cancelWorkflowId);
            },
            pending: cancelMutation.isPending,
            text: "Cancel run",
            theme: "destructive",
          }}
          secondaryButton={{
            disabled: cancelMutation.isPending,
            onClick: closeCancelModal,
            text: "Keep running",
          }}
          subtitle="This requests Temporal cancellation and marks the durable run as cancelled so the status card stops treating it as active."
          theme="error"
          title="Cancel migration run?"
          TitleIcon={StopIcon}
        >
          <Flex dir="column" gap="sm">
            <Text
              fontFamily="monospace"
              fontSize="xs"
              overflow="hidden"
              textColor="secondary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {cancelWorkflowId}
            </Text>
            {cancelMutation.error && (
              <Text fontSize="sm" textColor="error">
                {formatError(cancelMutation.error)}
              </Text>
            )}
          </Flex>
        </RedoModal>
      )}
    </>
  );
});
